import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  addCheapestDestination,
  editCheapestDestination,
  getCheapestDestinationById,
} from '../../../../api/endpoints/cmsContent';
import type { CheapestDestination, City } from '../../../../api/types/cmsContent';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { useFreshEditRow } from '../../../../components/useFreshEditRow';

const schema = z.object({
  fromCity_Id: z.coerce.number().min(1, 'Kalkış şehri gerekli'),
  fromCode: z.string().optional(),
  toCity_Id: z.coerce.number().min(1, 'Varış şehri gerekli'),
  toCode: z.string().optional(),
  startDate: z.string().optional(),
  price: z.coerce.number().optional(),
  priceCurrency: z.string().optional(),
  priority: z.coerce.number().optional(),
  link: z.string().optional(),
  type: z.string().optional(),
  airlineCode: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const emptyValues: FormValues = {
  fromCity_Id: undefined as unknown as number,
  fromCode: '',
  toCity_Id: undefined as unknown as number,
  toCode: '',
  startDate: '',
  price: 0,
  priceCurrency: 'TRY',
  priority: 0,
  link: '',
  type: 'DOM',
  airlineCode: '',
};

/** ISO date-time -> `YYYY-MM-DD` for the `type="date"` input, matching this codebase's
 *  existing convention (see modules/corporate/employee/components/EmployeeFormDialog.tsx). */
function toDateInputValue(iso?: string): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

function toFormValues(row: CheapestDestination): FormValues {
  return {
    fromCity_Id: row.fromCity_Id ?? (undefined as unknown as number),
    fromCode: row.fromCode ?? '',
    toCity_Id: row.toCity_Id ?? (undefined as unknown as number),
    toCode: row.toCode ?? '',
    startDate: toDateInputValue(row.startDate),
    price: row.price ?? 0,
    priceCurrency: row.priceCurrency ?? 'TRY',
    priority: row.priority ?? 0,
    link: row.link ?? '',
    type: row.type ?? 'DOM',
    airlineCode: row.airlineCode ?? '',
  };
}

/** Create/edit dialog for En Ucuz Rotalar (CheapestDestination). Confirmed via local-backend
 *  swagger + full live curl round-trip (see api/types/cmsContent.ts header — note the
 *  `Destionation` typo preserved on the wire, invisible here since the endpoint functions
 *  already hide it). `cities` is the current site's City list loaded by the list page, used
 *  for the "Kalkış Şehri"/"Varış Şehri" pickers (same FK-select precedent as
 *  modules/corporate/department/DepartmentFormDialog.tsx). Always rendered inside a specific
 *  site's tab, so `site_Id` is injected from the `siteId` prop. */
export function CheapestDestinationFormDialog({
  open,
  item,
  cities,
  siteId,
  onClose,
}: {
  open: boolean;
  /** null = create mode; otherwise edit mode for this row. */
  item: CheapestDestination | null;
  cities: City[];
  siteId: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!item;

  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  // Refetch the fresh row on edit-open; silently falls back to the list row on error.
  const seed = useFreshEditRow({
    open,
    editing: item,
    queryKey: ['cms', 'content', 'cheapestDestination', item?.id],
    fetch: async (row) => (await getCheapestDestinationById(row.id)).cheapestDestionations?.[0],
  });

  useEffect(() => {
    if (open) reset(seed ? toFormValues(seed) : emptyValues);
  }, [open, seed, reset]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      isEdit && item
        ? editCheapestDestination({ id: item.id, site_Id: siteId, ...values })
        : addCheapestDestination({ ...values, site_Id: siteId }),
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Rota güncellendi.' : 'Rota oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'cheapestDestinations'] });
      onClose();
    },
    onError: () => {
      /* surfaced via ApiErrorAlert below */
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Rotayı Güncelle' : 'Yeni Rota'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="fromCity_Id"
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    select
                    fullWidth
                    size="small"
                    label="Kalkış Şehri"
                    error={!!errors.fromCity_Id}
                    helperText={errors.fromCity_Id?.message}
                  >
                    {cities.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name ?? c.displayName ?? `#${c.id}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="toCity_Id"
                render={({ field }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    select
                    fullWidth
                    size="small"
                    label="Varış Şehri"
                    error={!!errors.toCity_Id}
                    helperText={errors.toCity_Id?.message}
                  >
                    {cities.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name ?? c.displayName ?? `#${c.id}`}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Kalkış Kodu (görüntü metni)" {...register('fromCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Varış Kodu (görüntü metni)" {...register('toCode')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Tarih"
                slotProps={{ inputLabel: { shrink: true } }}
                {...register('startDate')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Havayolu Kodu" {...register('airlineCode')} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="number" label="Fiyat" {...register('price')} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" label="Para Birimi" {...register('priceCurrency')} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <TextField fullWidth size="small" type="number" label="Öncelik" {...register('priority')} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Tip">
                    <MenuItem value="DOM">DOM</MenuItem>
                    <MenuItem value="INT">INT</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Link" {...register('link')} />
            </Grid>
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
